import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, Loader2, ShieldCheck } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { COVERAGE_OPTIONS, US_STATES } from "../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { full_name: "", email: "", phone: "", state: "", coverage: [], contact_preference: "phone", message: "" };
const field = "h-12 rounded-xl bg-white border-[#0B281E]/15 text-[#1A2621] placeholder:text-[#52635B]/70 focus-visible:ring-[#C88A35]";
const Label = ({ children, htmlFor }) => <label htmlFor={htmlFor} className="block font-mono-d text-[11px] uppercase tracking-[0.18em] text-[#52635B] mb-2">{children}</label>;

export const QuoteForm = () => {
  const [f, setF] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const toggle = (c) => setF({ ...f, coverage: f.coverage.includes(c) ? f.coverage.filter((x) => x !== c) : [...f.coverage, c] });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.state) return toast.error("Please select your state.");
    if (!f.coverage.length) return toast.error("Pick at least one coverage type.");
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, f);
      setDone(true);
      setF(EMPTY);
      toast.success("Request received — I'll be in touch soon.");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(Array.isArray(d) ? "Please check your details and try again." : d || "Something went wrong. Please call or email instead.");
    } finally {
      setLoading(false);
    }
  };

  if (done) return (
    <div data-testid="quote-success" className="rounded-[28px] bg-[#FAF8F5] text-[#0B281E] p-10 lg:p-14 min-h-[520px] flex flex-col justify-center">
      <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200, damping: 14 }} className="w-16 h-16 rounded-full bg-[#0B281E] text-[#E5A855] grid place-items-center"><Check className="w-8 h-8" /></motion.span>
      <h3 className="font-serif-d text-4xl lg:text-5xl mt-8">Thank you. You're in good hands.</h3>
      <p className="mt-4 text-[#52635B] max-w-md leading-relaxed">Your request is in. I'll review your details and reach out within one business day with options tailored to you.</p>
      <button onClick={() => setDone(false)} data-testid="quote-new-request-btn" className="mt-8 self-start text-sm font-semibold underline underline-offset-8">Submit another request</button>
    </div>
  );

  return (
    <form onSubmit={submit} data-testid="quote-form" className="rounded-[28px] bg-[#FAF8F5] text-[#1A2621] p-6 sm:p-10 lg:p-12 shadow-[0_40px_120px_-40px_rgba(0,0,0,0.6)]">
      <div className="grid sm:grid-cols-2 gap-5">
        <div className="sm:col-span-2"><Label htmlFor="qn">Full name</Label><Input id="qn" required minLength={2} value={f.full_name} onChange={set("full_name")} placeholder="Jane Doe" className={field} data-testid="quote-name-input" /></div>
        <div><Label htmlFor="qe">Email</Label><Input id="qe" type="email" required value={f.email} onChange={set("email")} placeholder="jane@email.com" className={field} data-testid="quote-email-input" /></div>
        <div><Label htmlFor="qp">Phone</Label><Input id="qp" type="tel" required minLength={7} value={f.phone} onChange={set("phone")} placeholder="(555) 123-4567" className={field} data-testid="quote-phone-input" /></div>
        <div>
          <Label>State</Label>
          <Select value={f.state} onValueChange={set("state")}>
            <SelectTrigger className={field} data-testid="quote-state-select"><SelectValue placeholder="Select your state" /></SelectTrigger>
            <SelectContent className="max-h-72 bg-white" data-lenis-prevent>{US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Best way to reach you</Label>
          <Select value={f.contact_preference} onValueChange={set("contact_preference")}>
            <SelectTrigger className={field} data-testid="quote-contact-pref-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{["phone", "text", "email"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-2">
          <Label>I'm interested in</Label>
          <div className="flex flex-wrap gap-2">
            {COVERAGE_OPTIONS.map((c, i) => {
              const on = f.coverage.includes(c);
              return (
                <motion.button whileTap={{ scale: 0.95 }} type="button" key={c} onClick={() => toggle(c)} data-testid={`quote-coverage-${i}`} aria-pressed={on}
                  className={`rounded-full px-4 py-2 text-sm border transition-colors ${on ? "bg-[#0B281E] text-[#FAF8F5] border-[#0B281E]" : "bg-white text-[#1A2621] border-[#0B281E]/15 hover:border-[#C88A35]"}`}>
                  {on && <Check className="inline w-3.5 h-3.5 mr-1 -mt-0.5 text-[#E5A855]" />}{c}
                </motion.button>
              );
            })}
          </div>
        </div>
        <div className="sm:col-span-2"><Label htmlFor="qm">Anything I should know? (optional)</Label><Textarea id="qm" value={f.message} onChange={set("message")} maxLength={2000} placeholder="Household size, current coverage, doctors you'd like to keep…" className="min-h-[110px] rounded-xl bg-white border-[#0B281E]/15 focus-visible:ring-[#C88A35]" data-testid="quote-message-input" /></div>
      </div>
      <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-5 justify-between">
        <p className="flex items-center gap-2 text-xs text-[#52635B]"><ShieldCheck className="w-4 h-4 text-[#C88A35]" /> Your info stays private. No spam, ever.</p>
        <button type="submit" disabled={loading} data-testid="quote-form-submit" className="inline-flex justify-center items-center gap-2 rounded-full bg-[#0B281E] text-[#FAF8F5] px-8 py-4 font-semibold hover:bg-[#C88A35] hover:text-[#071510] transition-colors disabled:opacity-60">
          {loading && <Loader2 className="w-4 h-4 animate-spin" />} {loading ? "Sending…" : "Request my free quote"}
        </button>
      </div>
    </form>
  );
};
